import React, { useMemo } from "react";
import type { FieldProps } from "@rjsf/utils";
import { cloneDeep, get, isEmpty, set } from "lodash";
import {
  DocumentUploadInput,
  MultiSelectInput,
  NumberInput,
  NumberWithUnitsInput,
  PropertyMarketValueInput,
  TagsInput,
  UniqueIdInput,
  UserInput,
} from "../../../../../../../Submissions/Fields";
import { BaseFieldPreview } from "./BaseFieldPreview";

export function NumberFieldPreview(props: FieldProps) {
  return (
    <BaseFieldPreview {...props}>
      <NumberInput {...props} />
    </BaseFieldPreview>
  );
}

export function NumberWithUnitsFieldPreview(props: FieldProps) {
  return (
    <BaseFieldPreview {...props}>
      <NumberWithUnitsInput {...props} />
    </BaseFieldPreview>
  );
}

export function MultiSelectFieldPreview(props: FieldProps) {
  const cleanedSchema = useMemo(() => {
    const oneOfOptions = get(props.schema, "items.oneOf") as
      | Array<{
          const: string;
          title: string;
        }>
      | undefined;
    const schema = cloneDeep(props.schema);
    const filteredOneOf =
      oneOfOptions?.filter(option => {
        return (
          option &&
          option.const &&
          !isEmpty(option.const) &&
          option.title &&
          !isEmpty(option.title)
        );
      }) || [];

    set(schema, "items.oneOf", filteredOneOf);

    return schema;
  }, [props.schema]);

  return (
    <BaseFieldPreview {...props}>
      <MultiSelectInput {...props} schema={cleanedSchema} />
    </BaseFieldPreview>
  );
}

export function TagFieldPreview(props: FieldProps) {
  const cleanedSchema = useMemo(() => {
    const itemsEnum = get(props.schema, "items.enum") as Array<string>;
    const schema = cloneDeep(props.schema);

    set(
      schema,
      "items.enum",
      itemsEnum.filter(value => !isEmpty(value)),
    );

    return schema;
  }, [props.schema]);

  return (
    <BaseFieldPreview {...props}>
      <TagsInput {...props} schema={cleanedSchema} />
    </BaseFieldPreview>
  );
}

export function UserFieldPreview(props: FieldProps) {
  return (
    <BaseFieldPreview {...props}>
      <UserInput {...props} />
    </BaseFieldPreview>
  );
}

export function UniqueIdFieldPreview(props: FieldProps) {
  let newPlaceholder = props.uiSchema?.["ui:prefix"] || "";

  newPlaceholder = newPlaceholder.replace(
    "{{year}}",
    new Date().getFullYear().toString(),
  );

  const numberType = props.uiSchema?.["ui:numberType"] || "ascending";

  if (numberType === "ascending") {
    newPlaceholder += "12345";
  } else {
    newPlaceholder += "41352";
  }

  const updatedProps = {
    ...props,
    uiSchema: {
      ...props.uiSchema,
      "ui:placeholder": newPlaceholder,
    },
  };

  return (
    <BaseFieldPreview {...updatedProps}>
      <UniqueIdInput {...updatedProps} />
    </BaseFieldPreview>
  );
}

export function DocumentUploadFieldPreview(props: FieldProps) {
  return (
    <BaseFieldPreview {...props}>
      <DocumentUploadInput {...props} />
    </BaseFieldPreview>
  );
}

export function PropertyMarketValueFieldPreview(props: FieldProps) {
  const cleanedProps = useMemo(() => {
    if (props.name === "propertyMarketValue") {
      let assessedValueIndex: Maybe<number>;

      return {
        ...props,
        schema: {
          ...props.schema,
          oneOf: props.schema.oneOf?.filter((option, index) => {
            const valueOption = option as { name: string };
            if (valueOption.name === "automatedAssessedValue") {
              assessedValueIndex = index;
              return false;
            }
            return true;
          }),
        },
        uiSchema: {
          ...props.uiSchema,
          oneOf: props.uiSchema?.oneOf.filter(
            (_: Record<string, unknown>, index: number) =>
              index !== assessedValueIndex,
          ),
        },
      };
    }

    return props;
  }, [props]);

  return (
    <BaseFieldPreview {...cleanedProps}>
      <PropertyMarketValueInput {...cleanedProps} />
    </BaseFieldPreview>
  );
}
